<script setup lang="ts">
import { trustStats } from "~/data/trust";

const { t, locale } = useI18n();
const route = useRoute();
const config = useRuntimeConfig();

const { data: page } = await useAsyncData(
  `page-contact-${locale.value}`,
  async () => {
    const collection = ("pages_" + locale.value) as "pages_en";
    return await queryCollection(collection)
      .where("slug", "=", "contact")
      .first();
  },
  { watch: [locale] }
);

const canonicalUrl = computed(
  () => `https://georgiadream.ge${route.path}`
);

// og:locale + og:locale:alternate for correct FB / Telegram preview when
// locale-prefixed contact URLs are shared. Pattern mirrors pages/index.vue.
const ogLocale = computed(() =>
  locale.value === "ru" ? "ru_RU" : locale.value === "ka" ? "ka_GE" : "en_US"
);
const ogLocaleAlternate = computed(() =>
  ["en_US", "ru_RU", "ka_GE"].filter((l) => l !== ogLocale.value)
);

const inLanguageTag = computed(() =>
  locale.value === "ru" ? "ru-RU" : locale.value === "ka" ? "ka-GE" : "en-US"
);

useHead({
  link: [{ rel: "canonical", href: canonicalUrl }],
});

useSeoMeta({
  title: () => page.value?.title,
  description: () => page.value?.description,
  ogTitle: () => page.value?.title,
  ogDescription: () => page.value?.description,
  ogImage: () => page.value?.hero_image,
  ogImageAlt: () => page.value?.hero_image_alt || page.value?.title,
  ogType: "website",
  ogUrl: canonicalUrl,
  ogLocale,
  ogLocaleAlternate,
});

useBreadcrumbsLd(() => [
  { name: t("nav.home"), path: "/" },
  { name: t("nav.contact"), path: "/contact" },
]);

// ContactPage with mainEntity → ContactPoint. Resolves cross-graph via @id
// against the sitewide Organization emitted by plugins/seo-organization.ts.
// availableLanguage matches the three site locales the owner answers in.
useHead(() => ({
  script: [
    {
      type: "application/ld+json",
      innerHTML: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "ContactPage",
        "@id": `${canonicalUrl.value}#contactpage`,
        url: canonicalUrl.value,
        inLanguage: inLanguageTag.value,
        isPartOf: { "@id": "https://georgiadream.ge/#website" },
        about: { "@id": "https://georgiadream.ge/#organization" },
        mainEntity: {
          "@type": "Organization",
          "@id": "https://georgiadream.ge/#organization",
          contactPoint: [
            {
              "@type": "ContactPoint",
              contactType: "Reservations",
              telephone:
                (config.public.whatsappNumber as string) || "+995505556117",
              email:
                (config.public.contactEmail as string) || "info@georgiadream.ge",
              availableLanguage: ["en", "ru", "ka"],
              areaServed: "GE",
            },
          ],
        },
      }),
    },
  ],
}));

useReveal();
</script>

<template>
  <div v-if="page">
    <section
      class="relative flex min-h-[40vh] w-full items-center overflow-hidden bg-cover bg-center pt-24 pb-10 sm:py-14 md:min-h-[52vh] md:py-20"
      :style="
        page.hero_image ? `background-image: url('${page.hero_image}')` : ''
      "
    >
      <div
        class="absolute inset-0 bg-gradient-to-b from-slate-900/60 via-slate-900/70 to-slate-900/90"
      />
      <div class="container relative z-10 text-center">
        <h1
          class="font-display text-3xl italic leading-tight text-balance [overflow-wrap:anywhere] text-white drop-shadow sm:text-4xl md:text-5xl lg:text-6xl"
        >
          {{ page.title }}
        </h1>
        <p
          v-if="page.intro"
          class="mx-auto mt-6 max-w-3xl text-lg text-white/90 md:text-xl"
        >
          {{ page.intro }}
        </p>
      </div>
    </section>

    <ContactStrip />

    <ContactPhoneCallout />

    <section
      class="relative overflow-hidden bg-gradient-to-b from-amber-50/40 via-white to-white dark:from-slate-900 dark:via-slate-900 dark:to-slate-900 pt-10 md:pt-14 pb-10 md:pb-14"
    >
      <div
        aria-hidden="true"
        class="pointer-events-none absolute -bottom-40 -left-32 size-[480px] rounded-full bg-amber-400/10 blur-3xl"
      />

      <div class="container relative">
        <div class="max-w-6xl mx-auto grid gap-10 lg:gap-14 lg:grid-cols-12 items-start">
          <aside class="lg:col-span-5" data-reveal>
            <p class="text-xs uppercase tracking-[0.3em] text-red-500/90 font-semibold">
              {{ t("contact_page.eyebrow_open_line") }}
            </p>
            <h2
              class="mt-4 font-display text-2xl md:text-3xl lg:text-4xl italic leading-tight text-slate-900 dark:text-white"
            >
              {{ t("contact_page.intro_heading") }}
            </h2>
            <p class="mt-4 inline-flex items-center gap-2 text-sm text-slate-500">
              <Icon name="mdi:clock-outline" size="18px" class="text-red-500" />
              {{ t("contact_page.response_time_value") }}
            </p>

            <div class="mt-10 space-y-4">
              <div class="flex items-center gap-3">
                <span
                  class="inline-flex items-center justify-center size-9 rounded-full bg-red-500/10 text-red-500 ring-1 ring-red-500/20"
                >
                  <Icon name="mdi:medal-outline" size="20px" />
                </span>
                <p class="text-sm text-slate-700 dark:text-slate-300">
                  {{ t("contact_page.trust_superhost_line", { year: trustStats.superhostSince }) }}
                </p>
              </div>
              <div class="flex items-center gap-3">
                <span class="inline-flex items-center gap-0.5 text-red-500">
                  <Icon name="mdi:star" size="18px" />
                  <Icon name="mdi:star" size="18px" />
                  <Icon name="mdi:star" size="18px" />
                  <Icon name="mdi:star" size="18px" />
                  <Icon name="mdi:star" size="18px" />
                </span>
                <p class="text-sm text-slate-700 dark:text-slate-300">
                  {{
                    t("contact_page.trust_rating_line", {
                      rating: trustStats.rating.toFixed(1),
                      count: trustStats.reviewsCount,
                    })
                  }}
                </p>
              </div>
            </div>
          </aside>

          <div class="lg:col-span-7">
            <ContactForm />
          </div>
        </div>
      </div>
    </section>
  </div>
</template>
